import { isLabelContentType, LabelContentItem, RestLabelResponse, setOrDeleteLabel } from '@tableau/data-catalog-shared';
import { restApiService } from '@tableau/vizportalclient-common-components';
import { call, put } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { SetCertificationAction } from '~src/catalog/actions/labels/SetCertificationAction';
import { CERTIFIED_INDICATOR_TYPE } from '~src/catalog/components/labels/DataQualityType';
import { defaultInitialState, DialogBodyProps, DialogState, getActionDialogOptions } from '~src/catalog/components/labels/LabelDialog';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../../codegen/Localize';

export function* handleSetCertification(action: SetCertificationAction) {
  try {
    const fn = FeatureFlags.viewCertificationEnabled ? openCertificationDialog : openCertificationDialogOld;
    yield call(openCertificationDialog, action);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function openCertificationDialog(action: SetCertificationAction): PromiseLike<unknown> {
  const { luid: contentId, name: contentName, contentType } = action.item;
  if (!isLabelContentType(contentType)) {
    throw new Error(`${action.item.contentType} is not a certifiable type`);
  }

  const contentItem: LabelContentItem = { contentId, contentName, contentType };

  return openCertificationDialogCatalog({ contentItem, action });
}

export function openCertificationDialogCatalog({ contentItem, action, onSubmit }: {
  readonly contentItem: LabelContentItem;
  readonly action?: SetCertificationAction;
  readonly onSubmit?: () => void;
}): Promise<unknown> {


  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
  const {makeJsonRestApiRequest} = restApiService({siteLuid});
  const doAction: (formData: DialogState) => Promise<unknown> = ({ luid, isActive, message, type }) => {
    return setOrDeleteLabel({
      luid: luid,
      category: 'certification',
      makeJsonRestApiRequest,
      contentItems: [contentItem],
      labelContent: {
        active: isActive,
        elevated: false,
        message: message,
        value: type
      }
    }).then(async _ => {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.labelUpdateSuccess({
          LABEL_CATEGORY: Messages.labelCategoryCertification(),
          CONTENT_TYPE: contentItem.contentType,
          CONTENT_NAME: contentItem.contentName,
         }),
      });
      if (onSubmit) {
        ToasterService.instance.markToastsAsDoNotDismissOnLocationChange();
        onSubmit();
      }
    }).catch(_ => ToasterService.instance.pop({
      type: ToastType.Error,
      text: Messages.dataQuality_setError({ CONTENT_NAME: contentItem.contentName })
    }));
  };

  const initialBodyProps: DialogBodyProps = { contentItem };
  const modalInstance = openReactActionDialog(getActionDialogOptions({
    initialBodyProps,
    initialFormData: defaultInitialState('certification'),
    doAction,
    labelCategory: 'certification'
  }));

  return modalInstance.result;
}

function setCertification(item: SetCertificationAction['item'], args: DialogState): Promise<RestLabelResponse | Record<string, unknown>> {
  const { contentType, luid } = item;
  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
  const {makeJsonRestApiRequest} = restApiService({siteLuid});

  if (!isLabelContentType(contentType)) {
    throw new Error(`${contentType} is not a certifiable type`);
  }

  if (!luid) {
    throw new Error('an item cannot be certified without a luid');
  }

  return setOrDeleteLabel({
    category: 'certification',
    makeJsonRestApiRequest,
    contentItems: [{ contentType, contentId: luid, contentName: item.name }],
    labelContent: { message: args.message, active: args.isActive, elevated: false, value: 'certified' },
  });
}

function getCertificationOrDefault(item: SetCertificationAction['item']): DialogState {
  const certification = item.dataQualityIndicators?.filter(i => i.type === CERTIFIED_INDICATOR_TYPE)?.shift() || null;

  if (certification) {
    return {
      isActive: certification.isActive,
      message: certification.message,
      labelCategory:'certification',
      type: '',
      isSevere: false,
    };
  }

  return {
    isActive: item.isCertified || false,
    message: item.certificationNote || '',
    labelCategory:'certification',
    type: '',
    isSevere: false,
  };
}

export function openCertificationDialogOld(action: SetCertificationAction) {
  const { contentType, luid: contentId, name: contentName } = action.item;

  if (!isLabelContentType(contentType)) {
    throw new Error(`${contentType} is not a certifiable type`);
  }

  const modalInstance = openReactActionDialog(getActionDialogOptions({
    initialBodyProps: {
      contentItem: {
        contentId,
        contentName,
        contentType,
      }
     },
    initialFormData: getCertificationOrDefault(action.item),
    doAction: (formData: any) => setCertification(action.item, formData),
    labelCategory: 'certification'
  }));

  return modalInstance.result;
}
